import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { TrendUp, TrendDown, ArrowLeft, Timer, CaretUp, CaretDown } from "phosphor-react";
import { Button } from "@/components/ui/button";
import GeniLoader from "../components/GeniLoader";

export default function EarlyExitsDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [allTrades, setAllTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  
  // Read setup filter from URL query params
  const setupFilterFromUrl = searchParams.get('setup');
  
  // Sort state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const tradesRes = await api.get('/trades');
      setAllTrades(tradesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load trades");
    } finally {
      setLoading(false);
    }
  };

  // Get trades with early exits using EXACT same logic as AdvancedAnalytics
  const earlyExitTrades = useMemo(() => {
    // First filter to closed trades
    let tradesToAnalyze = allTrades.filter(t => t.status === 'closed');
    
    // Apply setup filter if present
    if (setupFilterFromUrl) {
      if (setupFilterFromUrl === '__no_setup__') {
        tradesToAnalyze = tradesToAnalyze.filter(t => !t.setup_id);
      } else {
        tradesToAnalyze = tradesToAnalyze.filter(t => t.setup_id === setupFilterFromUrl);
      }
    }
    
    // Filter to eligible trades (must have RR data)
    const eligibleTrades = tradesToAnalyze.filter(t => 
      t.rr !== null && t.rr !== undefined
    );
    
    // Early exits:
    // 1. Wins: realized RR < planned RR * 0.8
    // 2. Losses: panic exit (loss too small, > -0.5R)
    const earlyExits = eligibleTrades.filter(t => {
      const realizedRR = t.rr;
      const plannedRR = t.original_rr;
      const hasOriginalRR = plannedRR !== null && plannedRR !== undefined;
      
      if (t.outcome === 'win' && hasOriginalRR && plannedRR > 0) {
        // Win: early exit if got less than 80% of planned RR
        return realizedRR < plannedRR * 0.8;
      } else if (t.outcome === 'loss') {
        // Loss: panic exit if loss is too small (didn't hold to stop)
        return realizedRR > -0.5 && realizedRR < 0;
      }
      return false;
    });
    
    return {
      trades: earlyExits,
      eligibleCount: eligibleTrades.length
    };
  }, [allTrades, setupFilterFromUrl]);

  // Cycle sort
  const cycleSort = (column) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Apply sorting
  const sortedTrades = useMemo(() => {
    let result = [...earlyExitTrades.trades];
    
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        if (sortColumn === 'date') {
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
        } else if (sortColumn === 'rr') {
          aVal = a.rr ?? 0;
          bVal = b.rr ?? 0;
        } else if (sortColumn === 'profit') {
          aVal = a.profit_usd ?? 0;
          bVal = b.profit_usd ?? 0;
        }
        
        return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
      });
    } else {
      // Default sort by date descending
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    
    return result;
  }, [earlyExitTrades.trades, sortColumn, sortDirection]);

  // Get exit type description
  const getExitType = (trade) => {
    if (trade.outcome === 'win') {
      const plannedRR = trade.original_rr || 0;
      const achievedPercent = plannedRR > 0 ? Math.round((trade.rr / plannedRR) * 100) : 0;
      return { type: 'win', label: 'Premature Profit', detail: `Got ${achievedPercent}% of target` };
    } else {
      return { type: 'loss', label: 'Panic Exit', detail: 'Closed before stop' };
    }
  };

  // Column header component
  const ColumnHeader = ({ label, column }) => (
    <th 
      className={`p-3 cursor-pointer select-none transition-colors hover:bg-white/5 ${
        sortColumn === column ? 'text-white' : ''
      }`}
      onClick={() => cycleSort(column)}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        {sortColumn === column && (
          sortDirection === 'desc' 
            ? <CaretDown size={12} weight="bold" className="text-[#FFA726]" />
            : <CaretUp size={12} weight="bold" className="text-[#FFA726]" />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="early-exits-detail-page">
      <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/advanced')}
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/5 p-2"
            data-testid="back-to-advanced"
          >
            <ArrowLeft size={20} weight="bold" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Timer size={28} weight="duotone" className="text-[#FFA726]" />
              <div>
                <div className="text-white/40 text-xs font-mono uppercase tracking-wider">EXECUTION QUALITY</div>
                <h2 className="text-3xl font-black font-heading tracking-tight" data-testid="page-title">
                  Early Exits
                </h2>
              </div>
            </div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm px-4 py-2">
            <span className="text-white/60 text-sm font-mono">TOTAL: </span>
            <span className="text-[#FFA726] font-bold text-lg" data-testid="total-count">{earlyExitTrades.trades.length}</span>
          </div>
        </div>

        {/* Summary Row */}
        <div className="mb-6" data-testid="early-exits-summary">
          <div className="text-white/40 text-xs font-mono uppercase tracking-wider mb-3">
            Summary
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-[#000000] border border-[#FFA726]/20 rounded-sm p-3">
                <div className="text-white/40 text-xs font-mono mb-1">EARLY EXITS</div>
                <div className="text-xl font-bold font-mono text-[#FFA726]" data-testid="summary-total">
                  {earlyExitTrades.trades.length}
                </div>
              </div>
              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                <div className="text-white/40 text-xs font-mono mb-1">% OF ELIGIBLE</div>
                <div className="text-xl font-bold font-mono text-white" data-testid="summary-percent">
                  {earlyExitTrades.eligibleCount > 0 
                    ? Math.round((earlyExitTrades.trades.length / earlyExitTrades.eligibleCount) * 100)
                    : 0}%
                </div>
              </div>
              <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                <div className="text-white/40 text-xs font-mono mb-1">CRITERIA</div>
                <div className="text-sm font-mono text-white/60">
                  &lt;80% target or panic exit
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {earlyExitTrades.trades.length === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <div className="w-16 h-16 bg-[#00E676]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Timer size={32} weight="duotone" className="text-[#00E676]/60" />
            </div>
            <h3 className="text-xl font-bold font-heading mb-2">No Early Exits</h3>
            <p className="text-white/60">
              Great job! You've been holding trades according to plan.
            </p>
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Trade List */}
            <div className="flex-1">
              <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                <table className="w-full" data-testid="early-exits-table">
                  <thead className="bg-[#0A0A0A]">
                    <tr className="text-left text-xs text-white/40 font-mono">
                      <th className="p-3">ASSET</th>
                      <th className="p-3">DIRECTION</th>
                      <th className="p-3">EXIT TYPE</th>
                      <ColumnHeader label="RR" column="rr" />
                      <ColumnHeader label="PROFIT" column="profit" />
                      <ColumnHeader label="DATE" column="date" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTrades.map((trade) => {
                      const exitInfo = getExitType(trade);
                      return (
                        <tr
                          key={trade.id}
                          data-testid={`trade-row-${trade.id}`}
                          onClick={() => setSelectedTrade(trade)}
                          className={`trade-row border-t border-white/10 cursor-pointer hover:bg-white/5 ${
                            selectedTrade?.id === trade.id ? "bg-white/5" : ""
                          }`}
                        >
                          <td className="p-3">
                            <div className="font-mono text-white font-bold">{trade.asset}</div>
                            <div className="text-xs text-white/40">{trade.timeframe || '-'}</div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {trade.direction === "long" ? (
                                <TrendUp size={16} weight="bold" className="text-[#00E676]" />
                              ) : (
                                <TrendDown size={16} weight="bold" className="text-[#FF3B30]" />
                              )}
                              <span className={`font-mono text-sm uppercase ${
                                trade.direction === "long" ? "text-[#00E676]" : "text-[#FF3B30]"
                              }`}>
                                {trade.direction}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 text-xs font-mono rounded ${
                              exitInfo.type === 'win' 
                                ? 'bg-[#00E676]/10 text-[#00E676]' 
                                : 'bg-[#FF3B30]/10 text-[#FF3B30]'
                            }`}>
                              {exitInfo.label}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`font-mono text-sm ${
                              (trade.rr ?? 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                            }`}>
                              {trade.rr != null ? trade.rr.toFixed(2) : '-'}R
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`font-mono text-sm ${
                              (trade.profit_usd ?? 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                            }`}>
                              {trade.profit_usd != null ? `$${trade.profit_usd.toFixed(2)}` : '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-white/60 text-sm">
                              {trade.created_at && format(new Date(trade.created_at), "MMM d, yyyy")}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Trade Details Panel */}
            {selectedTrade && (
              <div className="w-[400px] shrink-0">
                <div className="bg-[#121212] border border-white/10 rounded-sm p-6 sticky top-8" data-testid="trade-details-panel">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold font-heading">Trade Details</h3>
                    <Button
                      onClick={() => setSelectedTrade(null)}
                      variant="ghost"
                      size="sm"
                      className="text-white/40 hover:text-white hover:bg-white/5"
                    >
                      <ArrowLeft size={16} />
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {/* Chart Image */}
                    {selectedTrade.image_data && (
                      <div className="bg-[#000000] border border-white/10 rounded-sm overflow-hidden">
                        <img 
                          src={`data:image/png;base64,${selectedTrade.image_data}`}
                          alt="Trade chart"
                          className="w-full h-auto object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}

                    {/* Asset & Direction */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-black font-heading">{selectedTrade.asset}</div>
                        <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-bold ${
                          selectedTrade.direction === "long" 
                            ? "bg-[#00E676]/10 text-[#00E676]" 
                            : "bg-[#FF3B30]/10 text-[#FF3B30]"
                        }`}>
                          {selectedTrade.direction === "long" ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}
                          {selectedTrade.direction?.toUpperCase()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white/40 mb-1">EARLY EXIT</div>
                        <div className="px-2 py-1 rounded bg-[#FFA726]/10 text-[#FFA726] text-sm font-bold">
                          {getExitType(selectedTrade).label}
                        </div>
                      </div>
                    </div>

                    {/* Early Exit Explanation */}
                    <div className="bg-[#FFA726]/5 border border-[#FFA726]/20 rounded-sm p-4">
                      <div className="text-white/40 text-xs font-mono mb-2">WHY THIS IS AN EARLY EXIT</div>
                      <div className="text-white/80 text-sm">
                        {selectedTrade.outcome === 'win' ? (
                          <>
                            Achieved {selectedTrade.rr?.toFixed(2)}R but planned target was {selectedTrade.original_rr?.toFixed(2) || '?'}R.
                            {selectedTrade.original_rr && (
                              <span className="text-[#FFA726]"> ({Math.round((selectedTrade.rr / selectedTrade.original_rr) * 100)}% of target)</span>
                            )}
                          </>
                        ) : (
                          <>
                            Closed at {selectedTrade.rr?.toFixed(2)}R instead of holding to stop (-1R).
                            This suggests a fear-based exit.
                          </>
                        )}
                      </div>
                    </div>

                    {/* RR Comparison */}
                    {selectedTrade.original_rr && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">PLANNED RR</div>
                          <div className="text-white font-mono font-semibold">{selectedTrade.original_rr?.toFixed(2)}R</div>
                        </div>
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">ACHIEVED RR</div>
                          <div className={`font-mono font-semibold ${
                            (selectedTrade.rr ?? 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                          }`}>
                            {selectedTrade.rr?.toFixed(2)}R
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Price Levels */}
                    <div className="grid grid-cols-2 gap-4">
                      {selectedTrade.entry && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">ENTRY</div>
                          <div className="text-white font-mono font-semibold">${selectedTrade.entry?.toLocaleString()}</div>
                        </div>
                      )}
                      {selectedTrade.exit && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">EXIT</div>
                          <div className={`font-mono font-semibold ${
                            selectedTrade.outcome === 'win' ? 'text-[#00E676]' : 'text-[#FF3B30]'
                          }`}>
                            ${selectedTrade.exit?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedTrade.take_profit && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">PLANNED TP</div>
                          <div className="text-white/60 font-mono font-semibold">${selectedTrade.take_profit?.toLocaleString()}</div>
                        </div>
                      )}
                      {selectedTrade.hand_size && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">HAND SIZE</div>
                          <div className="text-white font-mono font-semibold">${selectedTrade.hand_size?.toFixed(2)}</div>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {selectedTrade.notes && (
                      <div className="pt-4 border-t border-white/10">
                        <div className="text-white/40 text-xs font-mono mb-2">NOTES</div>
                        <p className="text-white/80 text-sm whitespace-pre-wrap">{selectedTrade.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
